import { getCollections } from '@api/collections';
import {
  getDetailProduct,
  getProductCategoriesList,
  getProductsByStoreId,
  getStoreProductCategoriesWithProductsListV5,
  getStoreProducts,
} from '@api/product';
import { getStoreReviews } from '@api/rating';
import { getStore, getStoreCharges } from '@api/store';
import { getStoreTiming } from '@api/storetiming';
import { getVisitor } from '@api/visitors';
import { Container, Navbar } from '@components';
import { CartButton } from '@components/cartbutton/spa';
import { PLACEHOLDERS, SafeImage } from '@components/image';
import { CarouselContainer } from '@components/container/carousel';
import { Star } from '@components/star';
import {
  TCharge,
  TCollection,
  TData,
  TImage,
  TOption,
  TProduct,
  TProductCategory,
  TProductCategoryControl,
  TReview,
  TShopCategoryGroup,
  TShopCategoryV5Response,
  TStore,
  TStoreTiming,
} from '@components/types';
import { inventoryByVariantId } from '@components/variant/variantSelector';
import {
  formatAddress,
  getFilteredResults,
  getFirst,
  getSafeUrl,
  getSortedImages,
  humanizeCurrency,
  sanityIoImageLoader,
  staticImageLoader,
  trimToLength,
} from '@core/utils';
import { motion, useInView } from 'framer-motion';
import {
  BadgeCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Heart,
  Info,
  Phone,
  Search,
  X,
} from 'lucide-react';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Element, Events, Link as RLink, scrollSpy } from 'react-scroll';
import { SwiperSlide } from 'swiper/react';

import { NavbarV2 } from '@components/navbar/v2';
import MyCart from '@components/spa/cart';
import MyCollection from '@components/spa/collection';
import MyOrders from '@components/spa/myorders';
import SpecialProductCard from '@components/spa/product/card';
import SpecialProductCardV2 from '@components/spa/product/cardv2';
import appConfig from '@core/appconfig';
import { formatPhone } from "@core/utils";
import { AutoComplete, Input } from 'antd';
import { default as Moment, default as moment } from 'moment';
import { useRecoilState } from 'recoil';
import { selectedStoreState } from '@recoil/atoms/selectedstore';

interface PageProps {
  initialStore?: TStore;
  initialStoreTiming?: TStoreTiming[];
  initialReviews?: TReview[];
  initialFrequent?: TProduct[];
  initialCollections?: TCollection[];
  initialCharges?: TCharge[];
}

export const getServerSideProps: GetServerSideProps<PageProps> = async (
  context
) => {
  const { slug } = context.params;
  const storeId = parseInt(slug[0] as string);
  const [
    initialStore,
    initialStoreTiming,
    initialReviews,
    initialCollections,
    initialCharges,
  ] = await Promise.all([
    getStore(storeId),
    getStoreTiming(storeId),
    getStoreReviews(storeId, 0, 5),
    getCollections(storeId),
    getStoreCharges([storeId]),
  ]);
  return {
    props: {
      initialStore: initialStore || null,
      initialStoreTiming: initialStoreTiming || null,
      initialReviews: initialReviews || null,
      initialCollections: initialCollections || null,
      initialCharges: initialCharges || null,
    },
  };
};

const ShopDetail: React.FC = ({
  initialStore,
  initialStoreTiming,
  initialReviews,
  initialCollections,
  initialCharges,
}: PageProps) => {
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const router = useRouter();
  const [store, setStore] = useRecoilState<TStore>(selectedStoreState);
  const [searchQuery, setSearchQuery] = useState<string>(null);
  const [storeTimings, setStoreTimings] =
    useState<TStoreTiming[]>(initialStoreTiming);
  const [storeReviews, setStoreReviews] = useState<TReview[]>(initialReviews);
  const [productCategoriesList, setProductCategoriesList] = useState<
    TProductCategoryControl[]
  >([]);
  const [selectedProductCategory, setSelectedProductCategory] =
    useState<TProductCategoryControl>(null);
  const weekDays = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thrusday',
    'Friday',
    'Saturday',
  ];
  const pageSize = 10;
  const searchParams = useSearchParams();
  const [selectedProduct, setSelectedProduct] = useState<TProduct>();
  const [products, setProducts] = useState<TProduct[]>([]);
  const [skip, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isData, setIsData] = useState(true);
  const PAGE_LIMIT = 10;
  const [allCategories, setAllCategories] = useState<TShopCategoryV5Response[]>(
    []
  );
  const [storeCharges, setStoreCharges] = useState<TCharge[]>(initialCharges);
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);
  const [expandedSubCategories, setExpandedSubCategories] = useState<number[]>([]);
  const ref = useRef(null);
  const collapseRefs = useRef<{ [key: number]: HTMLDivElement }>({});
  const isInView = useInView(ref);
  const [collections, setCollections] =
    useState<TCollection[]>(initialCollections);
  const [productSuggestions, setProductSuggestions] = useState<TOption[]>([]);
  const [searchResult, setSearchResult] = useState<TProduct[]>();
  const [mode, setMode] = useState<string>('');
  const [transactionId, setTransactionId] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isMyOrdersOpen, setIsMyOrdersOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCollectionOpen, setIsCollectionOpen] = useState(false);
  const [shopSelectedProduct, setShopSelectedProduct] = useState<TProduct>(null)
  const [selectedCollection, setSelectedCollection] = useState<TCollection>();
  const [storeId, setStoreId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const hasOnlineOrdering = !!store?.storemeta?.functions?.online_ordering;
  const hasTableId = Number(router?.query?.table_id || 0) > 0;
  const [minutesUntilClose, setMinutesUntilClose] = useState(0);
  const hasInventory = selectedProduct?.inventories?.some((inventory) => inventory?.quantity > 0) || false;
  const isAlwaysAvailable = selectedProduct?.inventories?.some((inventory) => inventory?.always_available) || false;
  let openHours = store?.opening_hours?.find((hour) => hour?.day_of_week === moment()?.day());
  const curRunId = useRef<number>(0);
  const blankRows = Array(10).fill(0)
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [selectedRoom, setSelectedRoom] = useState(null)
  const [openTabType, setOpenTabType] = useState('home')
  const [showRoomServicePopup, setShowRoomServicePopup] = useState(false);
  const [chargesExpanded, setChargesExpanded] = useState(false);
  const noShowUnavailable = !!(
    (store?.storemeta as any)?.settings?.no_show_unavailable ||
    (store?.storemeta as any)?.no_show_unavailable
  );

  const isProductAvailable = useCallback((product: TProduct) => {
    if (product?.is_available === false) {
      return false;
    }

    if (!hasOnlineOrdering && !hasTableId) {
      return false;
    }

    if (isStoreClosed()) {
      return false;
    }

    return product?.inventories?.some(
      (inventory) =>
        inventory?.is_available !== false &&
        ((inventory?.quantity || 0) > 0 || inventory?.always_available)
    ) || false;
  }, [hasOnlineOrdering, hasTableId, openHours?.close_time, openHours?.open_time]);

  const filterProductsByAvailability = useCallback(
    (categoryProducts: TProduct[] = []) =>
      noShowUnavailable
        ? categoryProducts.filter((product) => isProductAvailable(product))
        : categoryProducts,
    [isProductAvailable, noShowUnavailable]
  );

  const visibleCategories = useMemo<TShopCategoryGroup[]>(() => {
    return allCategories
      .map((category: TShopCategoryV5Response) => {
        const rawSubCategories = category?.subcategories || [];
        const isSelfOnlySubCategory =
          rawSubCategories.length === 1 && rawSubCategories[0]?.id === category?.id;
        const ownProducts = filterProductsByAvailability(
          isSelfOnlySubCategory
            ? rawSubCategories[0]?.products || category?.products || []
            : category?.products || []
        );

        const subCategories = isSelfOnlySubCategory
          ? []
          : rawSubCategories
            .map((subCategory) => {
              const products = filterProductsByAvailability(subCategory?.products || []);

              return {
                ...subCategory,
                products,
                total_products: products.length,
              };
            })
            .filter(
              (subCategory) =>
                !noShowUnavailable || (subCategory?.products?.length || 0) > 0
            );

        const totalProducts =
          ownProducts.length +
          subCategories.reduce(
            (total, subCategory) =>
              total + (subCategory?.products?.length || 0),
            0
          );
        return {
          ...category,
          products: ownProducts,
          subCategories,
          total_products: totalProducts,
        };
      })
      .filter(
        (category) =>
          !noShowUnavailable ||
          (category?.products?.length || 0) > 0 ||
          (category?.subCategories?.length || 0) > 0
      );
  }, [allCategories, filterProductsByAvailability, noShowUnavailable]);

  // Product offers for the shop's JSON-LD (rich results). Capped to keep the head small.
  const productsJsonLd = useMemo(() => {
    const seen = new Set<number>();
    const items = [];
    for (const category of visibleCategories) {
      for (const product of [
        ...(category.products || []),
        ...category.subCategories.flatMap((sub) => sub.products || []),
      ]) {
        if (!product?.id || seen.has(product.id)) continue;
        const inventory =
          product.inventories?.find((inv) => inv?.price > 0 && inv?.is_available !== false) ||
          product.inventories?.find((inv) => inv?.price > 0);
        if (!inventory) continue;
        seen.add(product.id);
        const inStock =
          isProductAvailable(product) &&
          (inventory.always_available || (inventory.quantity || 0) > 0);
        items.push({
          '@type': 'Product',
          name: product.name,
          description: product.description || undefined,
          image: product.images?.[0]?.url || undefined,
          brand: product.brand?.name ? { '@type': 'Brand', name: product.brand.name } : undefined,
          gtin: product.gtin || undefined,
          offers: {
            '@type': 'Offer',
            price: inventory.price,
            priceCurrency: 'INR',
            availability: `https://schema.org/${inStock ? 'InStock' : 'OutOfStock'}`,
          },
        });
        if (items.length >= 100) return items;
      }
    }
    return items;
  }, [visibleCategories, isProductAvailable]);

  const visibleSearchResult = useMemo(
    () => filterProductsByAvailability(searchResult),
    [filterProductsByAvailability, searchResult]
  );

  const goBack = () => {
    setIsOpen(false);
    setIsCartOpen(false);
    setIsMyOrdersOpen(false);
    setIsCollectionOpen(false);
    setIsCategoryOpen(false);
    const referrer = document.referrer;
    if (typeof window != "undefined" && window.history.length > 1) {
      window.history.back();
    } else {
      router?.push(appConfig?.appName == "Jhattse" ? `/shop/${store?.id}/${getSafeUrl(store?.name)}` : `/stores`);
    }
  }

  const setOpenTabs = (current: string) => {
    if (current === 'home') {
      const referrer = document.referrer;
      const isInternal = referrer && referrer.includes(window.location.hostname);
      if (isInternal) {
        // User came from inside the same website → Go back
        window.history.back();
        return;
      }
      setIsCategoryOpen(false);
      setIsCartOpen(false);
      setIsMyOrdersOpen(false);
      setIsCollectionOpen(false);
      setOpenTabType('home')
    } else if (current === 'categories') {
      setIsCartOpen(false);
      setIsMyOrdersOpen(false);
      setIsCollectionOpen(false);
      setIsCategoryOpen(!isCategoryOpen);
      setOpenTabType('categories')
    } else if (current === 'cart') {
      setIsCategoryOpen(false);
      setIsMyOrdersOpen(false);
      setIsCollectionOpen(false);
      setIsCartOpen(!isCartOpen);
      setOpenTabType('cart')
    } else if (current === 'orders') {
      setIsCategoryOpen(false);
      setIsCartOpen(false);
      setIsCollectionOpen(false);
      setIsMyOrdersOpen(!isMyOrdersOpen);
      setOpenTabType('orders')
    }
  };

  const isStoreClosed = () => {
    let storeClosed = false;
    if (openHours?.open_time && openHours?.close_time) {
      const today = moment().format('YYYY-MM-DD');
      const openTime = moment(today + ' ' + openHours.open_time);
      const closeTime = moment(today + ' ' + openHours.close_time);
      const now = moment();
      storeClosed = !now.isBetween(openTime, closeTime, null, '[]'); // '[]' includes the endpoints
    }
    return storeClosed;
  }

  useEffect(() => {
    const { transaction_id, mode } = router.query;
    setStoreId(parseInt(router?.query?.slug[0]));
    if (mode != undefined) {
      setMode(mode as string);
    }
    if (transaction_id !== undefined) {
      setTransactionId(transaction_id as string);
      setIsMyOrdersOpen(true);
    }
  }, [router.isReady, router.query.slug]);

  async function fetchVisitor() {
    const response = await getVisitor(searchParams.get('visitor_id'));
    localStorage.setItem('visitorInfo', JSON.stringify(response));
  }

  function toggleAccordion(id: number, ensureOpen: boolean = false) {
    console.debug('toogling', id, ensureOpen);
    setExpandedCategories((current) => {
      if (ensureOpen) {
        return [id, ...current.filter((c) => c !== id)];
      }

      return current.includes(id)
        ? current.filter((c) => c !== id)
        : [...current, id];
    });
  }

  function toggleSubCategory(id: number, ensureOpen: boolean = false) {
    setExpandedSubCategories((current) => {
      if (ensureOpen) {
        return [id, ...current.filter((c) => c !== id)];
      }

      return current.includes(id)
        ? current.filter((c) => c !== id)
        : [...current, id];
    });
  }

  async function fetchCategories(storeId: number) {
    if (storeId > 0) {
      const categories: TShopCategoryV5Response[] =
        await getStoreProductCategoriesWithProductsListV5(storeId);
      setExpandedCategories([]);
      setExpandedSubCategories([]);
      setAllCategories(categories);
    }
  }

  useEffect(() => {
    setIsLoading(true);
    if (store?.id > 0) {
      getProductsByStoreId(store?.id, null, null, skip, PAGE_LIMIT)
        .then((data) => {
          if (skip === 0) {
            setProducts(data);
          } else {
            setProducts([...products, ...data]);
          }
          if (data.length == 0) {
            setIsData(false);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [store?.id, skip]);

  useEffect(() => {
    if (!isCartOpen || !isMyOrdersOpen || !isCollectionOpen) {
      // Registering the 'begin' event and logging it to the console when triggered.
      Events.scrollEvent.register('begin', (to, element) => {
        console.debug('begin', to, element);
      });

      // Registering the 'end' event and logging it to the console when triggered.
      Events.scrollEvent.register('end', (to, element) => {
        console.debug('end', to, element);
      });

      // Updating scrollSpy when the component mounts.
      scrollSpy.update();

      // Returning a cleanup function to remove the registered events when the component unmounts.
      return () => {
        Events.scrollEvent.remove('begin');
        Events.scrollEvent.remove('end');
      };
    }
  }, [isCartOpen, isMyOrdersOpen, isCollectionOpen]);

  useEffect(() => {
    setSkip(0);
  }, [router?.query]);

  useEffect(() => {
    if (isInView && isData && isLoading === false) {
      setSkip((prevSkip) => prevSkip + PAGE_LIMIT);
      setIsLoading(true);
    }
  }, [isInView, isData, isLoading]);

  const updateCategoryAndProduct = (
    category_id: number,
    products: TProduct[],
    allFetched: boolean
  ) => {
    let index = productCategoriesList?.findIndex((r) => r?.id == category_id);
    let categoryProduct = null;
    if (index === -1) {
      console.debug('Some weird problem');
    } else {
      categoryProduct = productCategoriesList[index];
      if (categoryProduct.isLoadMore) {
        categoryProduct.products = [...categoryProduct.products, ...products];
        categoryProduct.isLoadMore = !allFetched;
        categoryProduct.pageNumber = categoryProduct?.pageNumber + 1;
      }
    }
    setProductCategoriesList([...productCategoriesList]);
  };

  // Get All Products
  const getResults = (
    store_id: number,
    name: string,
    category_id: number = null,
    extend: boolean = true
  ) => {
    let index = productCategoriesList?.findIndex((r) => r?.id == category_id);
    let categoryProduct = null;
    if (index === -1) {
      console.debug('Some weird problem');
    } else {
      categoryProduct = productCategoriesList[index];
      setIsLoading(true);
      const res: Promise<TProduct[]> = getStoreProducts(
        category_id,
        store_id,
        name,
        categoryProduct?.pageNumber,
        pageSize
      );
      res.then((result) => {
        let allFetched = result?.length != pageSize;
        setIsLoading(false);
        updateCategoryAndProduct(category_id, result, allFetched);
      });
    }
  };

  // Get Store Timing
  const getAllStoreTiming = (store_id: number) => {
    getStoreTiming(store_id).then((result) => {
      setStoreTimings(result);
    });
  };

  useMemo(() => {
    if (searchParams.get('visitor_id')) {
      fetchVisitor();
    }
  }, [searchParams]);

  useEffect(() => {
    let id = (
      router?.query?.slug === undefined ? router?.query?.slug : router.query.slug[0]
    ) as string;
    let shop_product_id = router?.query?.product_id;
    if (!router.isReady && id == store?.id?.toString()) {
      return;
    }
    const res: Promise<TStore> = getStore(parseInt(id));
    res.then((store) => {
      setStore(store);

    });
    if (Number(shop_product_id) > 0) {
      getDetailProduct(parseInt(shop_product_id as string)).then((product) => {
        setShopSelectedProduct(product);
      })
    }
    if (router?.query?.source && router?.query?.table_id) {
      sessionStorage.setItem(
        'tableInfo',
        JSON.stringify({
          source: router?.query?.source,
          table: router?.query?.table,
          table_id: router?.query?.table_id,
          is_ac: router?.query?.is_ac,
        })
      );
    }
    if (router?.query?.room_id && router?.query?.source === "ROOM-SERVICE") {
      const room_id = router?.query?.room_id;
      const room_name = router?.query?.room_name;
      const source = router?.query?.source;
      setSelectedRoom({ id: Number(room_id), name: room_name, source: source })
      setShowRoomServicePopup(true);
    } else {
      setSelectedBooking(null)
      setSelectedRoom(null)
    }
  }, [router.isReady, router.query.slug]);

  useEffect(() => {
    if (store?.id > 0) {
      getAllStoreTiming(store?.id);
      getStoreReviews(store?.id, 0, 5).then((res) => {
        setStoreReviews(res);
      });
      if (selectedProductCategory?.id !== undefined) {
        getResults(store?.id, null, selectedProductCategory?.id);
      }
      getStoreCharges([store?.id]).then((result) => {
        setStoreCharges(result?.filter((charge:TCharge) => charge?.charge?.category != "booking"));
      });
    }
  }, [store]);

  // Handle Product Search Function
  const handleProductSearch = (value: string) => {
    setSearchQuery(value);
  };

  const getSearchResult = useCallback((
    storeId: number,
    searchQuery: string,
    runId: number
  ) => {
    setIsLoading(true);
    getStoreProducts(null, storeId, searchQuery, 0, 10).then(
      (result: TProduct[]) => {
        if (runId == curRunId.current) {
          const filteredResult = filterProductsByAvailability(result);
          setProductSuggestions(
            getFilteredResults(searchQuery, filteredResult as TData[])
          );
          setSearchResult(filteredResult)
        }
      }
    ).finally(() => {
      setIsLoading(false);
    });
  }, [filterProductsByAvailability]);

  useEffect(() => {
    if (searchQuery?.length > 0 && store?.id > 0) {
      curRunId.current += 1;
      getSearchResult(store?.id, searchQuery, curRunId.current);
    }
  }, [getSearchResult, searchQuery, store?.id]);

  // Fetch products on pagenumber change
  useEffect(() => {
    if (
      selectedProductCategory?.id !== undefined &&
      selectedProductCategory?.isLoadMore == true
    ) {
      getResults(
        store.id,
        searchQuery,
        selectedProductCategory?.id,
        selectedProductCategory?.pageNumber != 0
      );
    }
  }, [selectedProductCategory, store]);

  useEffect(() => {
    if (store?.id > 0) {
      // fetchFrequentlyOrdered(store?.id);
      // fetchFrequentlyAvailed(store?.id);
      fetchCategories(store?.id);
    }
  }, [store]);

  useEffect(() => {
    // Get overall Inventory
    if (store?.id > 0) {
      // Get products category list
      getProductCategoriesList(store?.id).then((result) => {
        setProductCategoriesList(
          result
            .sort((a: TProductCategory, b: TProductCategory) => a?.id - b?.id)
            .map((r: TProductCategory) => {
              return {
                ...r,
                products: [] as TProduct[],
                isLoadMore: true,
                pageNumber: 0,
              };
            })
        );
        if (result?.length > 0) {
          setSelectedProductCategory({
            ...result.sort(
              (a: TProductCategory, b: TProductCategory) => a?.id - b?.id
            )[0],
            products: [],
            isLoadMore: true,
            pageNumber: 0,
          });
        }
      });
      getCollections(store?.id).then((collections) => {
        setCollections(collections);
      });
    }
  }, [store]);

  useEffect(() => {
    const calculateMinutes = () => {
      if (openHours?.close_time) {
        const todayCloseTime = moment().format(`YYYY-MM-DD ${openHours?.close_time}`);
        const minutes = moment(todayCloseTime).diff(moment(), 'minutes');
        setMinutesUntilClose(minutes);
      }
    };

    // Calculate immediately
    calculateMinutes();

    // Update every minute
    const interval = setInterval(calculateMinutes, 60000);

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, [openHours?.close_time]);

  useEffect(() => {
    if (isCategoryOpen) {
      // Lock scrolling when cart is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scrolling when cart is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scroll is restored when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isCategoryOpen]);


  const renderCategoryProducts = (categoryProducts: TProduct[] = []) => {
    if (categoryProducts.length === 0) {
      return null;
    }

    return store?.storemeta?.settings?.grid_view ? (
      <div className="collapse-content py-4 grid grid-cols-2 gap-4 gap-y-6 justify-between">
        {categoryProducts.map((element) => {
          return (
            <SpecialProductCard
              key={element?.id}
              element={element}
              setSelectedProduct={setSelectedProduct}
              setIsOpen={setIsOpen}
              isOpen={isOpen}
            />
          );
        })}
      </div>
    ) : (
      <div className="collapse-content py-4 grid grid-cols-1 gap-4">
        {categoryProducts.map((element) => {
          return (
            <SpecialProductCardV2
              tableInfo={{
                source: router?.query?.source as string,
                table: router?.query?.table as string,
                table_id: router?.query?.table_id ? Number(router?.query?.table_id) : undefined,
                is_ac: router?.query?.is_ac === 'true'
              }}
              store={store}
              isStoreOpen={!isStoreClosed()}
              key={element?.id}
              element={element}
              setSelectedProduct={setSelectedProduct}
              setIsOpen={setIsOpen}
              isOpen={isOpen}
            />
          );
        })}
      </div>
    );
  };

  const renderCategoryContent = (category: TShopCategoryGroup) => (
    <>
      {renderCategoryProducts(category.products)}
      {category.subCategories?.length > 0 && (
        <div className="flex flex-col gap-1 pt-1">
          {category.subCategories.map((subCategory) => (
            <Element
              name={`category_${subCategory?.id}`}
              className="element"
              key={subCategory.id}
            >
              <div>
                <button
                  type="button"
                  style={{ backgroundColor: store?.storemeta?.settings?.shop_page_theme?.lightbg || "rgba(15,110,86,0.08)" }}
                  className="flex items-center justify-between w-full px-3 py-2 rounded-lg font-manrope cursor-pointer hover:brightness-105 transition-all"
                  onClick={() => toggleSubCategory(subCategory.id)}
                >
                  <span
                    style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }}
                    className="text-sm font-semibold text-neutral-700 flex items-center gap-2 min-w-0"
                  >
                    <span className="min-w-0 break-words text-left">{subCategory.name}</span>
                    {expandedSubCategories.includes(subCategory.id) ? (
                      <ChevronDown className="text-brand-500 flex-shrink-0" size={16} />
                    ) : (
                      <ChevronRight className="text-brand-500 flex-shrink-0" size={16} />
                    )}
                  </span>
                  <span
                    style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }}
                    className="text-xs font-medium text-neutral-500 whitespace-nowrap pl-2"
                  >
                    {subCategory.products?.length || 0} items
                  </span>
                </button>
                {expandedSubCategories.includes(subCategory.id) && renderCategoryProducts(subCategory.products)}
              </div>
            </Element>
          ))}
        </div>
      )}
    </>
  );
  return store != undefined ? (
    <div className="flex flex-col min-h-screen mx-auto relative ">
      <Head>
        <title>{hostname?.includes('jhattse') ? `${store?.name} - Jhattse` : `${store?.name}`}</title>
        <meta name="theme-color" content="#0F6E56" />
        <meta name="Keywords" content={`${store?.name},Jhattse,Local Stores`} />
        <meta
          name="Description"
          content={hostname?.includes('jhattse') ? `${store?.name} in ${formatAddress(
            store?.address,
            'India'
          )} offering products and services on Jhattse` : `${store?.name} is a store in ${formatAddress(store.address)} serving customers.`}
        />
        <link
          rel="canonical"
          href={`store/${store.id}/${getSafeUrl(store?.name)}`}
        />
        <meta property="og:title" content={`${store?.name} on ${appConfig.appName}`} />
        <meta
          name="og:description"
          content={hostname?.includes('jhattse') ? `${store?.name} in ${formatAddress(
            store?.address,
            'India'
          )} offering products and services on Jhattse` : `${store?.name} is a store in ${formatAddress(store.address)} serving customers.`}
        />
        {store?.image && <meta name="og:image" content={`${store?.image}`} />}
        <meta
          property="og:url"
          content={`https://jhattse.com/store/${store.id}/${getSafeUrl(
            store?.name
          )}`}
        />
        {store?.image != undefined && store?.image != null && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org/',
                '@type': 'ImageObject',
                contentUrl: store?.image,
                license: 'https://jhattse.com/terms',
                acquireLicensePage: 'https://jhattse.com/terms',
                creditText: `${appConfig?.appName}`,
                creator: {
                  '@type': 'Organization',
                  name: `${appConfig?.appName}`,
                },
                copyrightNotice: `${appConfig?.businessApp}`,
              }),
            }}
          />
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              {
                '@context': 'https://schema.org',
                '@type': 'LocalBusiness',
                logo: store.logo,
                image: [store.image],
                name: store.name,
                address: {
                  '@type': 'PostalAddress',
                  streetAddress: store?.address?.street_name || 'N/A',
                  addressLocality: store?.address?.locality || 'N/A',
                  postalCode: store?.address?.pincode || 'N/A',
                  addressCountry: 'IN',
                },
                review: (storeReviews || []).map((r: TReview) => {
                  return {
                    '@type': 'Review',
                    reviewRating: {
                      '@type': 'Rating',
                      ratingValue: r.rating,
                    },
                    author: {
                      '@type': 'Person',
                      name: r?.user?.full_name || 'Anonymous',
                    },
                  };
                }),
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: store?.stats?.rating_overall,
                  bestRating: '5',
                  reviewCount: store?.stats?.rating_count,
                },
                geo: {
                  '@type': 'GeoCoordinates',
                  latitude: store?.address?.latitude,
                  longitude: store?.address?.longitude,
                },
                url: `https://jhattse.com/store/${store?.id}/${getSafeUrl(
                  store?.name
                )}`,
                telephone: store?.phone,
                openingHoursSpecification: storeTimings
                  ?.filter(
                    (x) => x.open_time != undefined && x.close_time != undefined
                  )
                  .map((st) => {
                    return {
                      '@type': 'OpeningHoursSpecification',
                      dayOfWeek: weekDays[st.day_of_week],
                      opens: st.open_time,
                      closes: st.close_time,
                    };
                  }),
                menu: `https://jhattse.com/store/${store.id}/${getSafeUrl(
                  store?.name
                )}/menu`,
              },
              (key, value) => {
                // Check if key matches the string "pin" or "mob"
                // if matched return value "undefined"
                if (key === 'image') {
                  return store.image != undefined && store.image != null
                    ? value
                    : undefined;
                }
                if (key === 'logo') {
                  return store.logo != undefined && store.logo != null
                    ? value
                    : undefined;
                }
                if (key === 'geo') {
                  return store.address &&
                    store.address.latitude != undefined &&
                    store.address.longitude != undefined
                    ? value
                    : undefined;
                }
                if (key === 'aggregateRating') {
                  return store?.stats?.rating_count > 0 ? value : undefined;
                }
                if (key === 'review') {
                  return storeReviews?.length > 0 ? value : undefined;
                }
                return value;
              }
            ),
          }}
        />
        {productsJsonLd.length > 0 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'ItemList',
                name: `Products at ${store?.name}`,
                itemListElement: productsJsonLd.map((item, i) => ({
                  '@type': 'ListItem',
                  position: i + 1,
                  item,
                })),
              }),
            }}
          />
        )}
      </Head>
      {/* <Header backText={store?.name} /> */}
      <div style={{ background: store?.storemeta?.settings?.shop_page_theme?.bg || "#0F6E56" }} className={`max-w-[420px] w-full font-monrope mx-auto flex flex-row py-2.5 sticky top-0 z-50 shadow-md`}>
        <div className="relative flex flex-row justify-center items-center w-full">
          <div className="absolute top-0 left-2 h-full flex items-center">
            <button
              type="button"
              className="flex flex-col items-center"
              onClick={() => {
                goBack()
              }
              }
            >
              <ChevronLeft style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }} className="h-4 w-4 text-neutral-200" />
            </button>
          </div>
          <div style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }} className="flex flex-row items-center gap-1">
            <div className="flex-shrink-0 roundex-md overflow-hidden">
              <Image
                loader={sanityIoImageLoader}
                src={
                  store?.logo ||
                  store?.image ||
                  'https://cdn.jhattse.com/public/consumer/placeholder/store.png'
                }
                className="rounded-full max-w-16 h-8"
                alt={store?.name}
                priority={true}
                width="36"
                height="36"
              />
            </div>
            <span style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }} className="text-start md:text-base text-sm text-neutral-100 font-manrope font-semibold truncate overflow-ellipsis">
              {store?.name}
            </span>
            <BadgeCheck
              size={16}
              className="md:w-5 md:h-5 h-4 w-4 text-brand-500"
            />
          </div>
        </div>
      </div>
      <div
        className={`fixed ${isCategoryOpen ? 'bottom-0' : '-bottom-140'
          } w-full z-40 h-120 flex flex-col justify-center`}
      >
        <motion.div
          className="grid justify-items-stretch md:max-w-[420px] md:mx-auto md:w-full"
          animate={isCategoryOpen ? 'open' : 'closed'}
          variants={{
            open: { x: 0, y: '0px' },
            closed: { y: '200%' },
          }}
          transition={{ ease: 'easeOut', duration: 0.5 }}
        >
          <div
            style={{ background: store?.storemeta?.settings?.shop_page_theme?.bg || "#0F6E56" }}
            className="relative flex flex-col gap-2 justify-start items-center w-full max-h-140 h-fit z-20 rounded-t-3xl pb-8 shadow-2xl">
            <div className="w-10 h-1 rounded-full bg-white/20 mt-2"></div>
            <div className="flex flex-row">
              <div
                style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }}
                className="pt-3 pb-2 font-manrope text-base text-neutral-50 font-semibold">
                All Categories
              </div>
              <div
                style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }}
                className="absolute top-4 right-4 text-neutral-300 hover:text-brand-500 transition-colors cursor-pointer">
                <X
                  size={24}
                  onClick={(e) => {
                    setIsCategoryOpen(!isCategoryOpen);
                    setOpenTabType('home')
                  }}
                />
              </div>
            </div>
            <div
              className="flex flex-col w-full gap-2 overflow-y-scroll small-scrollbar"
              style={{ paddingBottom: '100px' }}
            >
              {visibleCategories.map((category) => {
                return (
                  <div className="px-4 font-manrope" key={category.id}>
                    <RLink
                      activeClass="active"
                      to={`category_${category?.id}`}
                      spy={true}
                      smooth={true}
                      offset={-104}
                      duration={500}
                      onClick={() => {
                        setIsCategoryOpen(!isCategoryOpen);
                        toggleAccordion(category?.id, true);
                      }}
                    >
                      <div
                        style={{ backgroundColor: store?.storemeta?.settings?.shop_page_theme?.lightbg || "rgba(255,255,255,0.08)" }}
                        className="flex items-center justify-between gap-2 px-3 text-neutral-100 rounded-xl py-2 border border-white/10 hover:border-brand-500/50 transition-colors">
                        <span style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }} className="font-medium min-w-0 break-words">{category?.name}</span>
                        <span style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }} className="text-xs px-2 py-0.5 rounded-full bg-brand-500/90 text-white font-semibold whitespace-nowrap">{category?.total_products || 0} Items</span>
                      </div>
                    </RLink>
                    {category?.subCategories?.length > 0 && (
                      <div className="mt-1.5 ml-3 flex flex-col gap-1.5 border-l border-white/10 pl-2.5">
                        {category.subCategories.map((subCategory) => (
                          <RLink
                            activeClass="active"
                            to={`category_${subCategory?.id}`}
                            spy={true}
                            smooth={true}
                            offset={-104}
                            duration={500}
                            key={subCategory.id}
                            onClick={() => {
                              setIsCategoryOpen(!isCategoryOpen);
                              toggleAccordion(category?.id, true);
                              toggleSubCategory(subCategory?.id, true);
                            }}
                          >
                            <div
                              style={{ backgroundColor: store?.storemeta?.settings?.shop_page_theme?.lightbg || "rgba(255,255,255,0.05)" }}
                              className="flex items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 border border-white/5 hover:border-brand-500/40 transition-colors"
                            >
                              <span style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }} className="text-sm font-medium min-w-0 break-words">{subCategory?.name}</span>
                              <span style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }} className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 font-medium whitespace-nowrap">{subCategory?.products?.length || 0} Items</span>
                            </div>
                          </RLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className='h-36'></div>
          </div>
        </motion.div>
      </div>
      <div
        className={`fixed ${isOpen ? 'bottom-0' : '-bottom-140'
          } w-full z-40 flex flex-col justify-center`}
      >
        <motion.div
          className="grid justify-items-stretch md:max-w-[420px] md:mx-auto md:w-full"
          animate={isOpen ? 'open' : 'closed'}
          variants={{
            open: { x: 0, y: '-64px', z: '100px' },
            closed: { y: '200%' },
          }}
          transition={{ ease: 'easeOut', duration: 0.5 }}
        >
          <div
            style={{ background: store?.storemeta?.settings?.shop_page_theme?.bg || "#0F6E56" }}
            className="relative flex flex-col gap-2 justify-start items-center w-full h-fit z-70 rounded-t-3xl py-4 shadow-2xl">
            <div className="w-10 h-1 rounded-full bg-white/20 -mt-1 mb-1"></div>
            <div className="flex justify-end w-full text-neutral-300">
              <span className="px-6 hover:text-brand-500 transition-colors cursor-pointer">
                <X
                  size={24}
                  style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }}
                  onClick={(e) => {
                    setIsOpen(!isOpen);
                    setOpenTabType('home');
                  }}
                />
              </span>
            </div>
            <div className="flex flex-col max-w-80 w-full gap-2">
              <div className="h-76 rounded-lg overflow-hidden flex flex-row gap-3 overflow-x-scroll no-scrollbar">
                <CarouselContainer direction="horizontal" loop={true}>
                  {getSortedImages(selectedProduct?.images)?.map(
                    (image: TImage, index: number) => {
                      return (
                        <SwiperSlide className="w-full h-fit">
                          <div className="h-76">
                            <Image
                              loader={sanityIoImageLoader}
                              className="absolute aspect-square inset-0 object-contain rounded-md"
                              width="320"
                              height="320"
                              src={image?.url}
                              alt={
                                image?.description ||
                                selectedProduct?.name
                              }
                              priority={index < 1}
                              loading={index < 1 ? 'eager' : 'lazy'}
                            />
                          </div>
                        </SwiperSlide>
                      );
                    }
                  )}
                </CarouselContainer>
              </div>
              <div
                style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }}
                className="flex flex-col flex-grow justify-between gap-1 font-medium">
                <div>
                  <p
                    style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }}
                    className="m-0 font-manrope text-neutral-50 text-base leading-snug line-clamp-3 font-semibold break-words py-0.5">
                    {(selectedProduct?.name || '').replace(/\s*\|\s*/g, ' · ')}
                  </p>
                </div>
                {selectedProduct?.description && (
                  <div className='flex gap-1 flex-wrap items-end py-0.5'>
                    <p
                      style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }}
                      className={`m-0 font-manrope text-neutral-300 text-sm leading-snug break-words`}>
                      {isExpanded ? selectedProduct?.description : trimToLength((selectedProduct?.description || 'N/A'), 100)}
                    </p>
                    {selectedProduct?.description.length > 100 && <button className="text-brand-500 font-semibold text-sm p-0 cursor-pointer hover:underline" onClick={() => setIsExpanded(!isExpanded)}>
                      {isExpanded ? 'Show Less' : 'Show More'}
                    </button>}
                  </div>
                )}
                <div className="flex flex-row justify-between">
                  <div>
                    <p
                      style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }}
                      className="m-0 font-manrope text-neutral-100 text-lg font-semibold">
                      {humanizeCurrency(selectedProduct?.inventories?.[0]?.price)}
                    </p>
                  </div>
                  {Math.round(
                    ((selectedProduct?.stats?.rating_count || 0) *
                      (selectedProduct?.stats?.rating_overall || 0)) /
                    5
                  ) > 0 && (
                      <div
                        style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }}
                        className="flex flex-row gap-1 items-center text-neutral-100">
                        {Math.round(
                          ((selectedProduct?.stats?.rating_count || 0) *
                            (selectedProduct?.stats?.rating_overall ||
                              0)) /
                          5
                        )}{' '}
                        <Heart className="text-red-600" />
                      </div>
                    )}
                </div>
                <div className="flex flex-row gap-4 items-center align-bottom md:w-44 w-full h-10 py-2">
                  {(hasOnlineOrdering || hasTableId) &&
                    (hasInventory || isAlwaysAvailable) && (!isStoreClosed()) ?
                    <CartButton
                      mode={''}
                      // textColor="text-saffron"
                      // btnStyle="h-8 bg-saffron text-white"
                      Isvariant={false}
                      store={store}
                      product={selectedProduct}
                      variantId={selectedProduct?.inventories[0]?.is_available && selectedProduct?.inventories[0]?.variant
                        ? selectedProduct?.inventories[0]?.variant?.id
                        : selectedProduct?.variants?.filter(
                          (variant) =>
                            getFirst(inventoryByVariantId(variant, selectedProduct))?.is_available ==
                            true
                        )[0]?.id}
                      inventory={selectedProduct?.inventories[0]}
                    ></CartButton>
                    :
                    <div className='px-1 text-center text-error-500 cursor-not-allowed text-xs font-semibold'>
                      {!(hasOnlineOrdering || hasTableId) ? 'Ordering not available' : isStoreClosed() ? 'Store closed' : 'Out of stock'}
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      <MyOrders
        isMyOrdersOpen={isMyOrdersOpen}
        setIsMyOrdersOpen={() => {
          setIsMyOrdersOpen(false);
          setOpenTabType('home');
        }}
        transactionId={transactionId}
        maxWidth="md:max-w-[420px] md:mx-auto md:w-full"
        mode={mode}
        store={store}
      />
      <MyCart
        store={store}
        noLink={true}
        isCartOpen={isCartOpen}
        maxWidth="md:max-w-[420px] md:mx-auto md:w-full"
        setIsCartOpen={() => {
          setIsCartOpen(false);
          setOpenTabType('home');
        }}
        setIsMyOrdersOpen={() => {
          setIsMyOrdersOpen(true);
          setOpenTabType('home');
        }}
        selectedBooking={selectedBooking}
        selectedRoom={selectedRoom}
      />
      <MyCollection
        isCollectionOpen={isCollectionOpen}
        setIsCollectionOpen={setIsCollectionOpen}
        collection={selectedCollection}
        setSelectedProduct={setSelectedProduct}
        store={store}
        maxWidth="md:max-w-[420px] md:mx-auto md:w-full"
        setIsOpen={setIsOpen}
        isOpen={isOpen}
      />
      <div
        style={{ background: store?.storemeta?.settings?.shop_page_theme?.mainbg || "" }}
        className={`max-w-[420px] w-full font-monrope mx-auto pb-10 ${(isCartOpen || isCollectionOpen || isMyOrdersOpen) ? 'scroll-smooth' : ''} bg-gradient-to-b from-[#EEF2FA] to-neutral-50`}>
        <div className="flex gap-4 flex-col mx-auto p-4">
          <div className="flex flex-row justify-between items-center bg-white rounded-2xl shadow-sm border border-neutral-200/70 p-3">
            <div className="flex flex-row gap-3 items-center">
              <div className="flex-shrink-0 rounded-xl overflow-hidden ring-2 ring-business-500/10">
                <Image
                  loader={sanityIoImageLoader}
                  src={
                    store?.logo ||
                    store?.image ||
                    'https://cdn.jhattse.com/public/consumer/placeholder/store.png'
                  }
                  className="rounded-xl max-w-24 h-12 object-cover"
                  alt={store?.name}
                  priority={true}
                  width="48"
                  height="48"
                />
              </div>
              <div className="flex flex-col font-manrope">
                <h1
                  style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }}
                  className="m-0 font-manrope text-[17px] leading-snug text-business-500 font-semibold break-words">
                  {store?.name}
                </h1>
                <span
                  style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }}
                  className="font-manrope text-sm text-neutral-600 font-medium">
                  {store?.category?.name}
                </span>
              </div>
            </div>
            {store?.stats?.rating_overall > 0 && <div className="flex flex-col justify-between items-end font-manrope">
              <Star rating={store?.stats?.rating_overall} />
              <div
                style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }}
                className="font-manrope text-sm text-neutral-600 font-medium whitespace-nowrap pb-1">
                {store?.stats?.rating_count} Ratings
              </div>
            </div>}
          </div>
          {
            shopSelectedProduct?.id > 0 &&
            <div className='bg-white border border-solid border-brand-500/40 rounded-2xl shadow-sm p-3'>
              <span className="inline-block mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-500 font-manrope">
                Selected product
              </span>
              <SpecialProductCardV2
                tableInfo={{
                  source: router?.query?.source as string,
                  table: router?.query?.table as string,
                  table_id: router?.query?.table_id ? Number(router?.query?.table_id) : undefined,
                  is_ac: router?.query?.is_ac === 'true'
                }}
                store={store}
                isStoreOpen={!isStoreClosed()}
                element={shopSelectedProduct}
                setSelectedProduct={setSelectedProduct}
                setIsOpen={setIsOpen}
                isOpen={isOpen}
              />
            </div>
          }
          {collections?.length > 0 && (
            <div className="space-y-2">
              <h6
                style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }}
                className="font-manrope text-base text-business-500 font-semibold flex items-center gap-1.5">
                <span className="w-1 h-4 rounded-full bg-brand-500 inline-block"></span>
                Collections
              </h6>
              <div className="flex flex-row overflow-x-scroll no-scrollbar">
                <CarouselContainer>
                  {collections?.map((collection: TCollection) => {
                    return (
                      <SwiperSlide
                        style={{ width: 'auto' }}
                        className="min-w-0"
                      >
                        <div
                          className="relative rounded-xl flex w-fit max-h-56 justify-center items-center border border-solid border-neutral-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                          onClick={() => {
                            setIsCollectionOpen(true);
                            setSelectedCollection(collection);
                          }}
                        >
                          <SafeImage
                            loader={sanityIoImageLoader}
                            src={collection?.image_url}
                            fallback={PLACEHOLDERS.store}
                            alt={collection?.title}
                            width="224"
                            height="224"
                            className="w-56 h-56 object-cover rounded-xl bg-amber"
                          />
                          <span className="absolute flex flex-col bottom-0 left-0 w-full max-h-56 bg-gradient-to-b from-transparent via-black/50 to-black md:h-16 h-12 rounded-xl pt-2">
                            <h2 className="m-0 font-manrope text-white font-semibold text-base leading-tight capitalize px-2 line-clamp-1">
                              {collection?.title}
                            </h2>
                            <h3 className="m-0 font-manrope text-neutral-100 text-xs font-medium line-clamp-1 px-2">
                              {collection?.subtitle}
                            </h3>
                          </span>
                        </div>
                        {/* </Link> */}
                      </SwiperSlide>
                    );
                  })}
                </CarouselContainer>
              </div>
            </div>
          )}
          {(storeCharges?.length > 0 && store?.storemeta?.settings?.hide_charges != true) && (
            <div className="flex flex-col w-full">
              <button
                type="button"
                onClick={() => setChargesExpanded(prev => !prev)}
                className="flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors cursor-pointer"
                style={{ backgroundColor: store?.storemeta?.settings?.shop_page_theme?.lightbg || "#f3f4f6" }}
              >
                <div className="flex items-center gap-2">
                  <Info size={13} style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "#374151" }} />
                  <span className="text-xs font-semibold"
                    style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "#374151" }}>
                    Additional Charges ({storeCharges.length})
                  </span>
                </div>
                <ChevronDown
                  size={14}
                  className="transition-transform duration-300"
                  style={{
                    color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "#374151",
                    transform: chargesExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                />
              </button>

              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${chargesExpanded ? 'max-h-40 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                <div className="flex md:flex-wrap overflow-x-scroll no-scrollbar charges-strip w-full gap-2">
                  {storeCharges?.map((charge) => (
                    <div key={charge?.charge?.id} className="flex gap-2 items-center justify-between px-2 py-1 bg-red-100 border border-solid border-red-300 rounded">
                      <span className="text-neutral-800 line-clamp-1 whitespace-nowrap dark:text-neutral-200 font-semibold text-xs titlecase">
                        {charge?.charge?.display_name || charge?.charge?.name}
                      </span>
                      <span className="text-neutral-800 dark:text-neutral-200 font-semibold text-xs">
                        {charge?.meta?.type === "PERCENTAGE" ? `${charge?.total_cost.toFixed(2) || 0}%` : `₹${(charge?.total_cost || 0).toFixed(1)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {selectedRoom && (
            <button
              onClick={() => router.push(`/stays/${store?.id}?asset_id=${selectedRoom.id}`)}
              className="flex items-center justify-between w-full rounded-xl px-4 py-3 border border-dashed border-amber-300 bg-amber-50 transition-all hover:bg-amber-100 active:scale-[0.98] cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg">🛎️</span>
                <div className="text-left">
                  <p className="text-xs font-bold text-amber-800">
                    Need something else?
                  </p>
                  <p className="text-xs text-amber-600">
                    Tap to raise a service request
                  </p>
                </div>
              </div>
              <ChevronRight size={14} className="text-amber-500 flex-shrink-0" />
            </button>
          )}
          {
            !store?.storemeta?.functions?.online_ordering || isStoreClosed() &&
            <div className='flex items-center gap-1.5 rounded-xl p-2.5 text-white bg-error-400 shadow-sm text-sm'>
              <Info color='#fff' size={14} />
              <span className='text-white text-sm font-medium'>Currently we are not taking online orders.</span>
            </div>
          }
          {(() => {
            return (minutesUntilClose > 0 && minutesUntilClose <= 60) && (
              <div className='flex items-center gap-1.5 rounded-xl p-2.5 text-white bg-brand-500 shadow-sm text-sm'>
                <Info color='#fff' size={14} />
                <span className='text-white text-sm font-medium'>
                  Store will close in {minutesUntilClose || 0} minutes
                </span>
              </div>
            );
          })()}
          <div className="space-y-4 lg:space-y-8 w-full">
            <div className="flex flex-col gap-2">
              <div
                style={{ background: store?.storemeta?.settings?.shop_page_theme?.mainbg || "" }}
                className={`flex gap-2 items-center py-2 sticky top-12 bg-[#EEF2FA] ${isCollectionOpen ||
                  isOpen ||
                  isCategoryOpen ||
                  isMyOrdersOpen ||
                  isCartOpen
                  ? 'z-12'
                  : 'z-40'
                  }`}
              >
                <div className="flex items-center w-full bg-white rounded-full shadow-sm border border-neutral-200/70 focus-within:border-business-500/40 focus-within:ring-2 focus-within:ring-business-500/10 transition-all">
                  <AutoComplete
                    open={false}
                    popupClassName="px-1 py-0.5"
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '999px',
                    }}
                    popupMatchSelectWidth={true}
                    size="middle"
                    options={productSuggestions}
                    value={searchQuery}
                    onSearch={handleProductSearch}
                    onSelect={(value, option) => setSearchQuery(option?.label)}
                    allowClear={true}
                  >
                    <Input
                      size="middle"
                      placeholder="Search products"
                      value={searchQuery}
                      prefix={<Search className="font-bold text-business-500" size="1.2em" />}
                      className="!rounded-full !border-0 !shadow-none !bg-transparent"
                    />
                  </AutoComplete>
                </div>
              </div>

              <div className="w-full min-h-screen">
                {isLoading ?
                  <div className="block">
                    <div className="flex flex-col gap-2">
                      {
                        blankRows?.map((x, i) =>
                          <div key={i} className={`grid grid-cols-9 lt-sm:grid-cols-7 animate-pulse gap-2 flex gap-2 p-6 bg-neutral-50 border-1 rounded-md `}>
                          </div>
                        )
                      }
                    </div>
                  </div> :
                  <div className="flex flex-col gap-2 collapse pb-8">
                    {searchQuery === null || searchQuery === '' ? (
                      visibleCategories.map((category) => (
                        <Element
                          name={`category_${category?.id}`}
                          className="element"
                          key={category.id}
                        >
                          <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            className="collapse-pannel"
                            key={category.id.toString()}
                            ref={(ref) =>
                              (collapseRefs.current[category.id] = ref)
                            }
                          >
                            <button
                              type="button"
                              style={{ background: store?.storemeta?.settings?.shop_page_theme?.bg || "#0F6E56" }}
                              className="collapse-header relative py-2.5 px-3 flex justify-between w-full items-center cursor-pointer font-manrope rounded-xl shadow-sm hover:brightness-110 transition-all"
                              onClick={() => toggleAccordion(category.id)}
                            >
                              <span
                                style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }}
                                className="font-manrope text-base text-neutral-100 flex items-center gap-2 font-semibold">
                                {category.name}
                                {expandedCategories.includes(category.id) ? (
                                  <ChevronDown className="text-brand-500" size={18} />
                                ) : (
                                  <ChevronRight className="text-brand-500" size={18} />
                                )}
                              </span>

                              <span
                                style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }}
                                className="font-manrope font-medium text-xs text-neutral-300 bg-white/10 px-2 py-0.5 rounded-full">
                                {category.total_products || 0} items
                              </span>
                            </button>
                            {expandedCategories.includes(category.id) && renderCategoryContent(category)}
                          </motion.div>
                        </Element>
                      ))
                    ) : (
                      <Element name={`product`} className="element">
                        <motion.div
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                        >
                          <>
                            {store?.storemeta?.settings?.grid_view ? (
                              <div className="collapse-content py-4 grid grid-cols-2 gap-4 gap-y-6 justify-between">
                                {visibleSearchResult?.map((product) => (
                                  <SpecialProductCard
                                    store={store}
                                    key={product?.id}
                                    element={product}
                                    setSelectedProduct={setSelectedProduct}
                                    setIsOpen={setIsOpen}
                                    isOpen={isOpen}
                                  />
                                ))}
                              </div>
                            ) : (
                              <div className="collapse-content py-4 grid grid-cols-1 gap-4 divide divide-neutral-500">
                                {visibleSearchResult?.map((product) => (
                                  <SpecialProductCardV2
                                    tableInfo={{
                                      source: router?.query?.source as string,
                                      table: router?.query?.table as string,
                                      table_id: router?.query?.table_id ? Number(router?.query?.table_id) : undefined,
                                      is_ac: router?.query?.is_ac === 'true'
                                    }}
                                    store={store}
                                    key={product?.id}
                                    element={product}
                                    setSelectedProduct={setSelectedProduct}
                                    setIsOpen={setIsOpen}
                                    isOpen={isOpen}
                                  />
                                ))}
                              </div>
                            )}
                          </>
                        </motion.div>
                      </Element>
                    )}
                  </div>}
              </div>
            </div>

            {!(searchQuery?.length > 0) && (
              <div className="flex flex-col pb-4">
                {store?.stats?.rating_count > 0 && (
                  <div className="pb-2">
                    <h2 className="font-manrope text-business-500 font-semibold text-base flex items-center gap-1.5">
                      <span className="w-1 h-4 rounded-full bg-brand-500 inline-block"></span>
                      Store Ratings{' '}
                      {store?.stats?.rating_count || 0
                        ? `(${store?.stats?.rating_count})`
                        : 'No'}{' '}
                    </h2>
                  </div>
                )}
                <div className="w-full">
                  <div className="flex flex-col gap-4 collapse">
                    {storeReviews?.map((review) => {
                      return (
                        <motion.div
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          className="collapse-pannel rounded-xl overflow-hidden"
                          key={review.id.toString()}
                          ref={(ref) => (collapseRefs.current[review.id] = ref)}
                        >
                          <div className="bg-white shadow-sm border border-neutral-200/70 p-4 rounded-xl">
                            <div className="grid grid-row grid-flow-col gap-2">
                              <div className="flex justify-start gap-2">
                                <Image
                                  loader={sanityIoImageLoader}
                                  src={
                                    review.user.profile_image ||
                                    '/consumer/circular-logo.png'
                                  }
                                  width="18"
                                  height="18"
                                  alt={`${review?.user?.first_name}`}
                                  loading="lazy"
                                />

                                <h3 className="text-neutral-900 font-medium">
                                  {review.user.first_name}{' '}
                                  {review.user.last_name}{' '}
                                </h3>
                              </div>

                              <span className="flex justify-end">
                                <Star rating={review?.rating || 0} />
                              </span>
                            </div>
                            <div>
                              <p className="text-neutral-700 pb-1">
                                {review.comment}
                              </p>
                            </div>
                            <div>
                              <p className="text-neutral-900 text-xs">
                                Posted on:{' '}
                                <span className="text-neutral-700">
                                  {Moment(review.added_on).format('YYYY/MM/DD')}{' '}
                                  at {Moment(review.added_on).format('HH:mm')}
                                </span>
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            {/* {!(searchQuery?.length > 0) && (
              <div className="flex flex-col pb-4 bg-nuetral-400">
                <span>{ }</span>
              </div>
            )
            } */}
            {(searchQuery === null || searchQuery === '') && (
              <div className="flex flex-col pb-8">
                <h2 style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }} className="font-manrope text-business-500 font-semibold text-base flex items-center gap-1.5">
                  <span className="w-1 h-4 rounded-full bg-brand-500 inline-block"></span>
                  {store?.legal_name}
                </h2>
                <div className="w-full">
                  <div className="flex flex-col gap-2 collapse">
                    <div className="flex flex-col">
                      <div className={`flex ${!store?.storemeta?.settings?.hide_contact ? "flex-col" : "flex-row-reverse justify-between items-center"}`}>
                        {store?.phone?.length > 0 && <div className="flex flex-row gap-2 justify-between items-center">
                          {!store?.storemeta?.settings?.hide_contact && <span style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }} className="font-medium text-neutral-700">
                            +91 {store?.phone}
                          </span>}
                          <div className="flex flex-row gap-2">
                            {!store?.storemeta?.settings?.hide_contact && <a
                              href={`tel:${store?.phone}`}
                              className="flex flex-row gap-2 justify-between items-center"
                            >
                              <button
                                type="button"
                                style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }}
                                className="flex items-center gap-1 hover:cursor-pointer bg-business-500 hover:brightness-110 transition-all shadow-sm text-sm text-neutral-50 py-1 px-3 rounded-full font-medium"
                              >
                                <Phone size={12} /> Call
                              </button>
                            </a>}
                            {!store?.storemeta?.settings?.hide_whatsapp && <a
                              href={`https://wa.me/${formatPhone(store?.phone)}?text=Hello`}
                              className="flex flex-row gap-2 justify-between items-center"
                              target="_blank"
                            >
                              <button
                                type="button"
                                style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }}
                                className="flex items-center gap-1 hover:cursor-pointer bg-green-600 hover:brightness-110 transition-all shadow-sm text-sm text-neutral-50 py-1 px-3 rounded-full font-medium"
                              >
                                <Image
                                  loader={staticImageLoader}
                                  src="business/whatsapp.webp"
                                  width="30"
                                  height="30"
                                  alt="Whatsapp Contact Link"
                                  className="rounded-sm w-4 h-4"
                                  loading="eager"
                                />
                                Whatsapp
                              </button>
                            </a>}
                          </div>
                        </div>}

                        {store?.email?.length > 0 && !store?.storemeta?.settings?.hide_email && <div
                          style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }} className="text-neutral-700">
                          <a
                            href={`mailto:${store?.email}`}
                            className="flex items-center"
                          ><span style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }} className="font-medium">{store?.email}</span></a>
                        </div>}
                      </div>
                      <p
                        style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }}
                        className="text-neutral-700">
                        <span className="font-medium text-sm">{`${Array([
                          store?.address?.street_name,
                          store?.address?.city?.name,
                          store?.address?.state?.name,
                        ])
                          .filter((x) => x?.length > 0)
                          .join(', ')}`}</span>
                      </p>
                    </div>
                    <div className="flex flex-row gap-2 flex-wrap">
                      <a href={`/shop/${store?.id}/privacy`}
                        style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }}
                        className="text-neutral-900 text-sm underline font-medium">Privacy Policy</a>
                      <a href={`/shop/${store?.id}/${store?.storemeta?.settings?.store_refund_policy || "refund"}`}
                        style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }}
                        className="text-neutral-900 text-sm underline font-medium">Refund Policy</a>
                      <a href={`/shop/${store?.id}/shipping`}
                        style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }}
                        className="text-neutral-900 text-sm underline font-medium">Shipping Policy</a>
                      <a href={`/shop/${store?.id}/terms`}
                        style={{ color: store?.storemeta?.settings?.shop_page_theme?.lighttext || "" }}
                        className="text-neutral-900 text-sm underline font-medium">Terms & Conditions</a>
                    </div>
                  </div>
                </div>
                {store?.storemeta?.gst?.managed_by?.length > 0 && <div className="mt-6 py-2 border-t border-neutral-200 bg-neutral-100">
                  <div className="flex items-center justify-center gap-1 text-sm">
                    <span
                      style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }}
                      className="text-neutral-600 font-semibold">Managed by:</span>
                    <div className="text-neutral-800">
                      <span style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "" }} className="font-semibold">{store?.storemeta?.gst?.managed_by}</span>
                    </div>
                  </div>
                </div>}
              </div>
            )}
          </div>
        </div>
      </div>
      <NavbarV2
        setOpenTabs={(tab: string) => {
          setOpenTabs(tab);
        }}
        openTabs={openTabType}
        maxWidth="md:max-w-[420px]"
        showInDesktop={true}
      />
      {showRoomServicePopup && selectedRoom && (
        <div className="fixed inset-0 z-[999] flex items-end justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowRoomServicePopup(false)}>
          <div
            className="w-full md:max-w-[420px] rounded-t-2xl p-6 pb-10 flex flex-col gap-4"
            style={{ background: store?.storemeta?.settings?.shop_page_theme?.bg || "#0F6E56" }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium"
                  style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "#ffffff99" }}>
                  Room · {selectedRoom.name}
                </p>
                <h2 className="text-lg font-bold text-white"
                  style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "#fff" }}>
                  What would you like?
                </h2>
              </div>
              <button onClick={() => setShowRoomServicePopup(false)}
                className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
                <X size={18} style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "#fff" }} />
              </button>
            </div>

            {/* Options */}
            <div className="flex flex-col gap-3">
              {/* Room Service Order */}
              <button
                onClick={() => setShowRoomServicePopup(false)}
                className="flex items-center gap-4 w-full rounded-xl p-4 text-left transition-all hover:opacity-90 active:scale-[0.98] cursor-pointer"
                style={{ backgroundColor: store?.storemeta?.settings?.shop_page_theme?.lightbg || "#ffffff15" }}
              >
                <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 text-2xl">
                  🍽️
                </div>
                <div>
                  <p className="font-bold text-white text-sm"
                    style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "#fff" }}>
                    Room Service Order
                  </p>
                  <p className="text-xs mt-0.5"
                    style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "#ffffff80" }}>
                    Order food & beverages to your room
                  </p>
                </div>
                <ChevronRight size={18} className="ml-auto flex-shrink-0"
                  style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "#ffffff60" }} />
              </button>

              {/* Service Request */}
              <button
                onClick={() => {
                  setShowRoomServicePopup(false);
                  router.push(
                    `/stays/${store?.id}?asset_id=${selectedRoom.id}`
                  );
                }}
                className="flex items-center gap-4 w-full rounded-xl p-4 text-left transition-all hover:opacity-90 active:scale-[0.98] cursor-pointer"
                style={{ backgroundColor: store?.storemeta?.settings?.shop_page_theme?.lightbg || "#ffffff15" }}
              >
                <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 text-2xl">
                  🛎️
                </div>
                <div>
                  <p className="font-bold text-white text-sm"
                    style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "#fff" }}>
                    Service Request
                  </p>
                  <p className="text-xs mt-0.5"
                    style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "#ffffff80" }}>
                    Housekeeping, maintenance & more
                  </p>
                </div>
                <ChevronRight size={18} className="ml-auto flex-shrink-0"
                  style={{ color: store?.storemeta?.settings?.shop_page_theme?.text || "#ffffff60" }} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  ) : (
    <Container>
      <div className="w-full">
        <div className="h-12 bg-neutral-200 dark:bg-neutral-700 max-w-[480px] mb-2.5"></div>
      </div>
      <div className="h-2"></div>
      <div className="space-y-8 m-5 animate-pulse md:space-y-0 md:space-x-8 md:flex md:items-center">
        <div className="flex justify-center items-center max-w-96 h-48 bg-neutral-300 rounded dark:bg-neutral-700">
          <svg
            className="w-12 h-12 text-neutral-200"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            fill="currentColor"
            viewBox="0 0 640 512"
          >
            <path d="M480 80C480 35.82 515.8 0 560 0C604.2 0 640 35.82 640 80C640 124.2 604.2 160 560 160C515.8 160 480 124.2 480 80zM0 456.1C0 445.6 2.964 435.3 8.551 426.4L225.3 81.01C231.9 70.42 243.5 64 256 64C268.5 64 280.1 70.42 286.8 81.01L412.7 281.7L460.9 202.7C464.1 196.1 472.2 192 480 192C487.8 192 495 196.1 499.1 202.7L631.1 419.1C636.9 428.6 640 439.7 640 450.9C640 484.6 612.6 512 578.9 512H55.91C25.03 512 .0006 486.1 .0006 456.1L0 456.1z" />
          </svg>
        </div>
        <div className="w-full">
          <div className="h-2.5 bg-neutral-200 rounded-full dark:bg-neutral-700 w-48 mb-4 "></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[480px] mb-2.5"></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 mb-2.5"></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[440px] mb-2.5"></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[460px] mb-2.5"></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[360px]"></div>
          <div className="h-2"></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[460px] mb-2.5"></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[460px] mb-2.5"></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[460px] mb-2.5"></div>
        </div>
        <span className="sr-only">Loading...</span>
      </div>
      <Navbar />
    </Container>
  );
};

export default ShopDetail;
